import axios from 'axios';
import Cookies from 'js-cookie';
import auth from './auth.js';

import { VUE_APP_API_URL } from '@/utilities/constants.js';
const baseURL = VUE_APP_API_URL;
const instance = axios.create({ baseURL });

// Lazy-load router to avoid circular dependency:
// api/axios -> router -> store -> modules -> authentication -> api/axios
let _router = null;
const getRouter = () => {
  if (!_router) _router = require('@/router').default;
  return _router;
};

const listOfPublicViews = [
  '/view-projects',
  '/profile',
  '/showcase',
  '/invitation',
  '/terms-and-conditions',
  '/privacy-policy',
  '/signup',
  '/direct-registration',
  '/projects',
  '/creativities',
  '/itineraries',
  '/around-me',
  '/login',
  '/latest-members',
  '/latest-showcases',
  '/latest-itineraries',
  '/profile-showcases'
];

instance.interceptors.request.use(
  function(request) {
    const bearerToken = Cookies.get('bearerToken');
    const ksToken = Cookies.get('ksBearerToken');

    const publicMode =
      listOfPublicViews.some(route => {
        return window.location.href.includes(route);
      }) && !bearerToken;

    if (
      request.url !== '/auth/login' &&
      !request.url.includes('member-Ofs') &&
      !request.url.includes('/accounts/reset/password/') &&
      !request.url.includes('/accounts/signup/') &&
      !publicMode
    ) {
      request.headers['Authorization'] = ksToken || bearerToken;
    }
    return request;
  },
  function(err) {
    return Promise.reject(err);
  }
);

// Shared refresh promise prevents multiple simultaneous refresh attempts
// (e.g. when several API calls all get 401 at once, or across tabs sharing cookies)
let refreshPromise = null;

instance.interceptors.response.use(undefined, function(err) {
  // Network errors (offline, blocked, CORS, abort) have no err.response —
  // reject early so we don't crash trying to read .status, and so the caller sees the failure.
  if (!err.response) return Promise.reject(err);

  //if error is not 401 && url is not to refresh token reject promise
  if (err.response.status !== 401 && err.config.url !== '/auth/refresh-token')
    return Promise.reject(err);

  //if error comes from refresh token endpoint remove cookies and storage redirect to login
  if (err.config.url === '/auth/refresh-token') {
    refreshPromise = null;
    Cookies.remove('bearerToken');
    Cookies.remove('ksBearerToken');
    localStorage.removeItem('vuex');
    localStorage.setItem('logout_event', Date.now().toString());
    getRouter().push('/login?authError=true');
    return Promise.reject(err);
  }

  if (err && err.response) {
    const validPath = listOfPublicViews.some(route => {
      return window.location.href.includes(route);
    });

    //check if token is available
    const bearerToken = Cookies.get('bearerToken');
    const ksToken = Cookies.get('ksBearerToken');
    const token = ksToken || bearerToken;
    const type = ksToken ? 'ksBearerToken' : 'bearerToken';

    if (token) {
      // Only start one refresh at a time — subsequent 401s wait for the same refresh
      if (!refreshPromise) {
        refreshPromise = auth.refresh(token, type).finally(() => {
          refreshPromise = null;
        });
      }
      return refreshPromise.then(() => {
        // Retry through `instance` so a second 401 can also trigger refresh/cleanup.
        // Drop the stale Authorization — the request interceptor will set it from the freshly-stored cookie.
        delete err.config.headers['Authorization'];
        return instance.request(err.config);
      });
      //handle error — no token available
    } else if (
      !validPath &&
      !err.response.request.responseURL.includes('/auth/user')
    ) {
      const intendedRoute = window.location.pathname;
      const lastRoute = localStorage.getItem('lastRoute');

      const route =
        (intendedRoute !== '/login' && intendedRoute !== '/dashboard')
          ? intendedRoute
          : lastRoute;

      getRouter().push(`/login?routeTo=${route}`);
      return Promise.reject(err);
    } else {
      return Promise.reject(err);
    }
  }
});

export default instance;
